import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDistance = (m) => {
  if (m == null) return '';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
};

export const RelayPointSelector = ({ postalCode, city, country, carrier, value, onChange, language }) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!postalCode || !city) {
      setPoints([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/shipping/parcel-points`, {
        params: { postal_code: postalCode, city, country: country || 'FR', carrier },
      })
      .then(({ data }) => {
        if (!cancelled) setPoints(data.points || []);
      })
      .catch(() => {
        if (!cancelled) {
          setPoints([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postalCode, city, country, carrier]);

  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-border/50" data-testid="relay-point-selector">
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#0F4C5C]" />
        {language === 'fr' ? 'Choisissez votre point relais' : 'Choose your pickup point'}
      </h4>

      {!postalCode || !city ? (
        <p className="text-sm text-muted-foreground" data-testid="relay-need-address">
          {language === 'fr'
            ? 'Renseignez votre ville et code postal ci-dessus pour afficher les points relais à proximité.'
            : 'Fill in your city and postal code above to see nearby pickup points.'}
        </p>
      ) : loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground" data-testid="relay-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          {language === 'fr' ? 'Recherche des points relais…' : 'Searching pickup points…'}
        </div>
      ) : error || points.length === 0 ? (
        <p className="text-sm text-amber-700" data-testid="relay-empty">
          {language === 'fr'
            ? 'Aucun point relais trouvé pour cette adresse. Vérifiez la ville et le code postal.'
            : 'No pickup point found for this address. Check the city and postal code.'}
        </p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="relay-points-list">
          {points.map((p) => (
            <div
              key={p.code}
              onClick={() => onChange(p)}
              className={`p-3 rounded-lg border cursor-pointer transition-colors text-sm ${
                value?.code === p.code
                  ? 'border-[#0F4C5C] bg-[#0F4C5C]/5'
                  : 'border-border bg-white hover:border-[#0F4C5C]/50'
              }`}
              data-testid={`relay-point-${p.code}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDistance(p.distance_m)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {p.street}, {p.postal_code} {p.city}
              </p>
            </div>
          ))}
        </div>
      )}

      {value && (
        <p className="text-xs text-emerald-700 mt-3" data-testid="relay-selected-info">
          {language === 'fr' ? 'Point relais sélectionné :' : 'Selected pickup point:'} <strong>{value.name}</strong> ({value.code})
        </p>
      )}
    </div>
  );
};
